import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { EmailTemplate, emailTemplateService } from '@/services/emailTemplateService';
import { Monitor, Smartphone } from 'lucide-react';
import { templateLabel } from '@/components/design/emailTemplateMeta';

/** Accepts stored colours as HSL triplets ("188 100% 35%") or hex. */
const col = (v: string) => (v.trim().startsWith('#') || v.trim().startsWith('hsl') ? v.trim() : `hsl(${v})`);

interface EmailPreviewProps {
  template: EmailTemplate;
}

export function EmailPreview({ template }: EmailPreviewProps) {
  const [viewMode, setViewMode] = useState<'desktop' | 'mobile'>('desktop');
  
  // Get mock data for preview
  const mockData = emailTemplateService.getMockData(template.template_type);
  const { subject, content } = emailTemplateService.processTemplate(template, mockData);

  const containerClass = viewMode === 'mobile' 
    ? 'max-w-sm mx-auto' 
    : 'max-w-2xl mx-auto';

  // Safe color fallbacks
  const safeColors = {
    background_color: template.color_config?.background_color || '0 0% 100%',
    text_color: template.color_config?.text_color || '0 0% 20%',
    button_color: template.color_config?.button_color || '188 100% 35%',
    button_text_color: template.color_config?.button_text_color || '0 0% 100%'
  };

  const emailStyle = {
    backgroundColor: `${col(safeColors.background_color)}`,
    color: `${col(safeColors.text_color)}`,
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
    lineHeight: '1.6'
  };

  const buttonStyle = {
    backgroundColor: `${col(safeColors.button_color)}`,
    color: `${col(safeColors.button_text_color)}`,
    padding: '12px 24px',
    borderRadius: '6px',
    textDecoration: 'none',
    display: 'inline-block',
    fontWeight: '600',
    border: 'none',
    cursor: 'pointer'
  };

  const isCamera = template.template_type === 'camera_alert' || template.template_type === 'camera_image';
  const cc = (template.content_config || {}) as Record<string, any>;
  const sample = { camera_name: 'North Gate', ai_subject: 'Vehicle blocking site gate', organisation: 'Snapshot Live' };
  const fill = (t: string) => t.replace(/\{\{\s*(\w+)\s*\}\}/g, (_, k) => (sample as any)[k] ?? '');
  const esc = (t: string) => t.replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]!));
  const cameraSubject = isCamera ? fill(template.subject_template || (template.template_type === 'camera_alert' ? 'Alert: {{ai_subject}} – {{camera_name}}' : '{{ai_subject}} – {{camera_name}}')) : '';
  const cameraHtml = isCamera ? (() => {
    const primary = col(template.color_config?.primary_color || '#321521');
    const accent = col(template.color_config?.secondary_color || '#DFEF97');
    const bg = col(template.color_config?.background_color || '#DFD4C9');
    const text = col(template.color_config?.text_color || '#321521');
    const detail = col(template.color_config?.text_color || '#6b5a60');
    const button = col(template.color_config?.button_color || template.color_config?.primary_color || '#321521');
    const btnText = col(template.color_config?.button_text_color || '#ffffff');
    const logo = template.logo_url ? `<img src="${esc(template.logo_url)}" style="max-height:48px;max-width:200px;display:block;margin:0 auto;" />` : `<div style="font-size:22px;font-weight:700;color:${primary};">Snapshot Live</div>`;
    const badgeBg = col(cc.badge_background || template.color_config?.secondary_color || '#DFEF97');
    const badgeFg = col(cc.badge_text_color || template.color_config?.button_text_color || '#ffffff');
    const badge = `<table role="presentation" cellspacing="0" cellpadding="0" border="0" style="margin:0 0 12px;"><tr><td bgcolor="${badgeBg}" style="background:${badgeBg};color:${badgeFg};font-size:12px;font-weight:700;padding:4px 10px;border-radius:12px;">${esc(cc.badge_text || (template.template_type === 'camera_alert' ? 'ALERT' : 'NEW IMAGE'))}</td></tr></table>`;
    const intro = cc.intro ? `<p style="font-size:15px;line-height:1.6;margin:0 0 12px;">${esc(fill(cc.intro))}</p>` : '';
    const img = cc.show_image !== false ? `<div style="height:200px;border-radius:8px;margin:0 0 20px;background:${accent};text-align:center;line-height:200px;color:${primary};font-size:13px;">Latest camera image</div>` : '';
    const row = (k: string, v: string) => `<tr><td style="padding:4px 12px 4px 0;font-size:13px;color:${detail};">${k}</td><td style="padding:4px 0;font-size:13px;color:${text};">${v}</td></tr>`;
    const description = template.template_type === 'camera_alert' ? 'A vehicle has been parked across the main gate for over 10 minutes.' : 'No issues detected in the latest image.';
    const reason = template.template_type === 'camera_alert' ? 'The gate is obstructed and needs attention.' : 'The latest image confirms the site is clear.';
    return `<!DOCTYPE html><html><body style="margin:0;background:${bg};font-family:Arial,Helvetica,sans-serif;color:${text};"><div style="max-width:600px;margin:0 auto;">
<div style="padding:24px;text-align:center;">${logo}</div><div style="padding:8px 28px 28px;">${badge}
 <h1 style="font-size:22px;margin:0 0 12px;">${esc(sample.ai_subject)}</h1>${intro}
  <p style="font-size:15px;line-height:1.6;margin:0 0 12px;">${description}</p><p style="font-size:14px;line-height:1.6;margin:0 0 20px;"><strong>Notification reason:</strong> ${reason}</p>${img}
<table style="border-collapse:collapse;margin:0 0 24px;">${row('Camera', 'North Gate')}${row('Location', 'Site entrance')}${row('Time', '27 Sep 2026, 14:32')}</table>
 <table role="presentation" cellspacing="0" cellpadding="0" border="0" align="center"><tr><td bgcolor="${button}" style="background:${button};border-bottom:3px solid ${accent};border-radius:8px;"><span style="display:inline-block;background:${button};color:${btnText};padding:12px 28px;border-radius:8px;">${esc(cc.cta_text || 'View camera')}</span></td></tr></table></div>
<div style="border-top:1px solid ${text};padding:16px 24px;text-align:center;font-size:12px;">${esc(cc.footer || 'Snapshot Live')}<br/><u>${esc(cc.unsubscribe_text || 'Stop these alerts')}</u></div></div></body></html>`;
  })() : '';

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle>Email Preview</CardTitle>
          <div className="flex gap-2">
            <Button
              variant={viewMode === 'desktop' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setViewMode('desktop')}
            >
              <Monitor className="h-4 w-4" />
            </Button>
            <Button
              variant={viewMode === 'mobile' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setViewMode('mobile')}
            >
              <Smartphone className="h-4 w-4" />
            </Button>
          </div>
        </div>
        <div className="flex flex-wrap gap-2 text-sm">
          <Badge variant="secondary">{templateLabel(template.template_type)}</Badge>
          <Badge variant="outline">Sample data</Badge>
        </div>
      </CardHeader>
      <CardContent>
        {isCamera ? (
          <div className={containerClass}>
            <div className="mb-4 p-3 bg-muted rounded">
              <div className="text-sm text-muted-foreground">Subject:</div>
              <div className="font-medium">{cameraSubject}</div>
            </div>
            <iframe title="Camera email preview" className="w-full h-[640px] rounded-lg border bg-card" srcDoc={cameraHtml} />
          </div>
        ) : (
        <div className={containerClass}>
          {/* Email Header */}
          <div className="mb-4 p-3 bg-muted rounded">
            <div className="text-sm text-muted-foreground">Subject:</div>
            <div className="font-medium">{subject}</div>
          </div>

          {/* Email Body */}
          <div 
            className="border rounded-lg p-6 space-y-4"
            style={emailStyle}
          >
            {/* Header with logo */}
            <div className="text-center border-b pb-4 mb-6">
              {template.logo_url ? (
                <a 
                  href={template.logo_url ? '#' : '#'} 
                  style={{ textDecoration: 'none' }}
                >
                  <img 
                    src={template.logo_url || "/lovable-uploads/e3bcf967-66a2-4e29-8728-54617b830190.png"}
                    alt="Email template logo"
                    className="h-16 w-auto mx-auto mb-2"
                  />
                </a>
              ) : (
                <img 
                  src="/lovable-uploads/e3bcf967-66a2-4e29-8728-54617b830190.png"
                  alt="Email template logo"
                  className="h-16 w-auto mx-auto mb-2"
                />
              )}
              <p className="text-sm opacity-80">Device Monitoring & Management Platform</p>
            </div>

            {/* Greeting */}
            {template.content_config?.greeting && (
              <h1 style={{ color: `${col(safeColors.text_color)}`, fontSize: '28px', fontWeight: 'bold', margin: '0 0 10px 0', textAlign: 'center' }}>
                {template.content_config.greeting}
              </h1>
            )}
            
            {/* Strapline */}
            {template.content_config?.strapline && (
              <p style={{ color: `${col(safeColors.text_color)}`, fontSize: '18px', margin: '0 0 30px 0', textAlign: 'center', fontWeight: '500', opacity: '0.8' }}>
                {template.content_config.strapline}
              </p>
            )}

            {/* Greeting */}
            {content.greeting && (
              <p className="text-lg">{content.greeting}</p>
            )}

            {/* Introduction */}
            {content.intro && (
              <p>{content.intro}</p>
            )}

            {/* Description */}
            {content.description && (
              <p>{content.description}</p>
            )}

            {/* Proposal Details (for proposal notifications) */}
            {content.proposal_details && (
              <div className="bg-muted p-4 rounded-lg">
                <p>{content.proposal_details}</p>
              </div>
            )}

            {/* Benefits (hidden for partner invitations) */}
            {template.template_type !== 'partner_invitation' && content.benefits && content.benefits.length > 0 && (
              <div>
                <h3 className="font-semibold mb-2">Key Benefits:</h3>
                <ul className="list-disc list-inside space-y-1">
                  {content.benefits.map((benefit, index) => (
                    <li key={index}>{benefit}</li>
                  ))}
                </ul>
              </div>
            )}

            {/* Key Benefits from template config (hidden for partner invitations) */}
            {template.template_type !== 'partner_invitation' && template.content_config?.benefits && Array.isArray(template.content_config.benefits) && template.content_config.benefits.length > 0 && (
              <div>
                <h3 style={{ color: `${col(safeColors.text_color)}`, fontSize: '20px', fontWeight: '600', margin: '0 0 16px 0' }}>Key Benefits:</h3>
                <ul style={{ margin: '0', paddingLeft: '20px' }}>
                  {template.content_config.benefits.map((benefit: string, index: number) => (
                    <li key={index} style={{ color: '#475569', fontSize: '16px', lineHeight: '1.6', margin: '0 0 8px 0' }}>
                      {benefit}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Call to Action */}
            {content.cta_text && (
              <div className="text-center py-4">
                <button style={buttonStyle}>
                  {content.cta_text}
                </button>
              </div>
            )}

            {/* Footer */}
            {content.footer && (
              <div className="border-t pt-4 mt-6 text-sm text-muted-foreground">
                <p>{content.footer}</p>
              </div>
            )}

            {/* Standard Footer */}
            <div className="border-t pt-4 mt-6 text-xs text-muted-foreground text-center">
              <p>© 2025 Snapshot Live. All rights reserved.</p>
              <p>This is a preview using mock data.</p>
            </div>
          </div>
        </div>
        )}
      </CardContent>
    </Card>
  );
}